import { Grid } from '@factorialco/deprecated-design-system'
import { WorkingConditionVersionApplicationFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import centsToDecimal from 'modules/core/lib/formatCents'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import Section from 'modules/contracts/components/ContractDetails/Section'
import BlankSection from 'modules/contracts/components/ContractDetails/Section/Blank'
import useCanSeeYearlyBalance from 'modules/contracts/hooks/useCanSeeYearlyBalance'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type Employee from 'modules/employees/store/models/Employee'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import AnnualWorkingTimeCalculation from './AnnualWorkingTimeCalculation'
import BankHolidayTreatment from './BankHolidayTreatment'
import MaxWeeklyHours from './MaxWeeklyHours'
import MaxYearlyHours from './MaxYearlyHours'
import WorkableDays from './WorkableDays'
import WorkingDayType from './WorkingDayType'
import WorkingHours from './WorkingHours'

interface Props {
  contractVersion: ContractVersion
  employee: Employee
  workingConditionVersionApplication?: WorkingConditionVersionApplicationFragment
}

const Time = ({ contractVersion, employee, workingConditionVersionApplication }: Props) => {
  const { loading, canSeeYearlyBalance } = useCanSeeYearlyBalance()
  const canEdit = policyAuthorizations.for('contracts.edit', employee)
  const canSeeWorkingDayType = policyAuthorizations.for('contracts.see_working_day_type', employee)
  const canSeeWorkingHours = policyAuthorizations.for('contracts.see_working_hours', employee)

  if (!canSeeWorkingDayType && !canSeeWorkingHours) return null
  if (loading) return <Loading />

  const hasSpanishWorkingDayType =
    contractVersion.isSpanish && contractVersion.attributes.es_working_day_type_id
  const hasFrenchWorkingDayType =
    contractVersion.isFrench && contractVersion.attributes.fr_work_type_id

  const hasWorkHours = !!contractVersion.attributes.working_hours
  const hasWorkingFrequency = !!contractVersion.attributes.working_hours_frequency && hasWorkHours
  const hasWorkableDays = !!contractVersion.attributes.working_week_days
  const hasYearlyHours = !!contractVersion.attributes.max_legal_yearly_hours
  const hasWeeklyHours = !!contractVersion.attributes.maximum_weekly_hours
  const hasBankHolidayTreatment =
    hasFeature('dev_time_tracking_workable_days_definition') &&
    !!contractVersion.attributes.bank_holiday_treatment

  const showEmptySlate =
    canEdit &&
    !hasSpanishWorkingDayType &&
    !hasFrenchWorkingDayType &&
    !hasWorkingFrequency &&
    !hasWorkableDays &&
    !hasYearlyHours &&
    !hasWeeklyHours
  const workingHours = hasWorkHours
    ? centsToDecimal(contractVersion.attributes.working_hours ?? 0)
    : undefined
  const workingHoursFrequency = contractVersion.attributes.working_hours_frequency
  const showWorkingDayType =
    !showEmptySlate &&
    canSeeWorkingDayType &&
    (canEdit || hasSpanishWorkingDayType || hasFrenchWorkingDayType)
  const showWorkingHours = !showEmptySlate && canSeeWorkingHours && (canEdit || hasWorkingFrequency)
  const showWorkableDays = !showEmptySlate && canSeeWorkingHours && (canEdit || hasWorkableDays)
  const showMaxYearlyHours =
    canSeeYearlyBalance && !showEmptySlate && canSeeWorkingHours && (canEdit || hasYearlyHours)
  const showAnnualWorkingTimeCalculation =
    hasFeature('dev_time_tracking_annualisation_daily_value') &&
    canSeeYearlyBalance &&
    !showEmptySlate &&
    canSeeWorkingHours &&
    (canEdit || hasYearlyHours)
  const showMaxWeeklyHours =
    canSeeYearlyBalance && !showEmptySlate && canSeeWorkingHours && (canEdit || hasWeeklyHours)
  const showBankHolidayTreatment = !showEmptySlate && canSeeWorkingHours && hasBankHolidayTreatment

  if (
    !showWorkingDayType &&
    !showWorkingHours &&
    !showWorkableDays &&
    !showMaxYearlyHours &&
    !showMaxWeeklyHours &&
    !showBankHolidayTreatment &&
    !showEmptySlate
  )
    return null

  return (
    <Section icon="clock" title={i18n.t('contract_versions.time.title')}>
      <Grid variant="fixed">
        {showWorkingDayType && (
          <WorkingDayType contractVersion={contractVersion} canEdit={canEdit} />
        )}
        {showWorkingHours && (
          <WorkingHours
            workingHours={workingHours}
            frequency={workingHoursFrequency ?? undefined}
          />
        )}
        {showWorkableDays && (
          <WorkableDays workableDays={contractVersion.attributes.working_week_days ?? ''} />
        )}
        {showMaxYearlyHours && (
          <MaxYearlyHours
            maxYearlyHoursInCents={contractVersion.attributes.max_legal_yearly_hours ?? 0}
            workingConditionVersionApplication={workingConditionVersionApplication}
          />
        )}
        {showMaxWeeklyHours && (
          <MaxWeeklyHours
            maxWeeklyHoursInCents={contractVersion.attributes.maximum_weekly_hours ?? 0}
          />
        )}
        {showBankHolidayTreatment && (
          <BankHolidayTreatment
            bankHolidayTreatment={contractVersion.attributes.bank_holiday_treatment ?? ''}
            workingConditionVersionApplication={workingConditionVersionApplication}
          />
        )}
        {showAnnualWorkingTimeCalculation && (
          <AnnualWorkingTimeCalculation
            annualWorkingTimeDistribution={
              contractVersion.attributes.annual_working_time_distribution ?? undefined
            }
            workdaysCount={contractVersion.attributes.annual_workdays_count ?? undefined}
            adjustedDailyMinutes={contractVersion.attributes.adjusted_daily_minutes ?? undefined}
          />
        )}
        {showEmptySlate && (
          <BlankSection
            employee={employee}
            contractVersion={contractVersion}
            icon="timer"
            title={i18n.t('contract_versions.time.blank_slate.title')}
            description={i18n.t('contract_versions.time.blank_slate.description')}
            buttonText={i18n.t('contract_versions.time.blank_slate.button')}
          />
        )}
      </Grid>
    </Section>
  )
}

export default observer(Time)
